"use client";
import React, { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaChevronLeft,
  FaChevronRight,
  FaBalanceScale,
  FaLandmark,
  FaUserTie,
  FaFileInvoice,
  FaShieldAlt,
  FaCalculator,
  FaBriefcase,
  FaArrowRight,
  FaFileInvoiceDollar
} from "react-icons/fa";

function PopularGuide() {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const { scrollLeft } = scrollRef.current;
      const firstCard = scrollRef.current.querySelector(".slide-card-new");
      const gap = window.innerWidth <= 768 ? 20 : 30;
      const itemWidth = firstCard ? firstCard.offsetWidth + gap : 380;

      const scrollTo =
        direction === "left"
          ? scrollLeft - itemWidth
          : scrollLeft + itemWidth;

      scrollRef.current.scrollTo({ left: scrollTo, behavior: "smooth" });

      // Silent snap back to create infinite loop
      setTimeout(() => {
        if (!scrollRef.current) return;
        const currentScroll = scrollRef.current.scrollLeft;

        if (currentScroll < itemWidth) {
          scrollRef.current.scrollTo({
            left: currentScroll + itemWidth * 4,
            behavior: "auto",
          });
        }
        else if (currentScroll > itemWidth * 7) {
          scrollRef.current.scrollTo({
            left: currentScroll - itemWidth * 4,
            behavior: "auto",
          });
        }
      }, 500);
    }
  };

  const originalServices = [
    {
      title: "Regulatory Compliance",
      desc: "Helping businesses stay compliant with industry regulations and legal requirements.",
      icon: <FaBalanceScale />,
      theme: "#165986",
      light: "#dbeafe"
    },
    {
      title: "Corporate Governance",
      desc: "Building strong governance structures for transparency and business growth.",
      icon: <FaLandmark />,
      theme: "#059669",
      light: "#d1fae5"
    },
    {
      title: "Employment Law",
      desc: "Professional legal support for employment policies and workplace compliance.",
      icon: <FaUserTie />,
      theme: "#7c3aed",
      light: "#ede9fe"
    },
    {
      title: "ITR E-Verification",
      desc: "Fast and secure Income Tax Return verification with digital compliance solutions.",
      icon: <FaFileInvoice />,
      theme: "#ea580c",
      light: "#ffedd5"
    },
  ];

  // Duplicate for infinite loop
  const services = [...originalServices, ...originalServices, ...originalServices];

  useEffect(() => {
    if (scrollRef.current) {
      const firstCard = scrollRef.current.querySelector(".slide-card-new");
      if (firstCard) {
        const gap = window.innerWidth <= 768 ? 20 : 30;
        const itemWidth = firstCard.offsetWidth + gap;
        scrollRef.current.scrollLeft = itemWidth * 4;
      }
    }
  }, []);

  return (
    <section className="legal-section-new">
      {/* Decorative Backgrounds */}
      <div className="bg-shape-glow"></div>
      <div className="bg-grid-pattern"></div>

      <div className="container-fluid px-4 px-lg-5 position-relative">
        <div className="legal-hero-wrapper">
          <div className="row align-items-center">

            {/* LEFT COLUMN */}
            <div className="col-lg-6 left-col-new">
              <div className="sub-tag-new">
                <span className="icon-circle"><FaFileInvoiceDollar /></span>
                <span>TRUST &bull; COMPLIANCE &bull; GROWTH</span>
                <div className="line-extend"></div>
              </div>

              <h2 className="main-legal-h2">
                Our Legal Compliance
                <span className="text-highlight">Solutions</span>
              </h2>

              <p className="desc-text-new">
                We provide smart legal compliance solutions including governance, taxation,
                employment law, and regulatory consulting services.
              </p>

              <div className="pills-row-new">
                <div className="pill-item-new pill-green">
                  <FaShieldAlt className="icon" /> Corporate Compliance
                </div>
                <div className="pill-item-new pill-cyan">
                  <FaCalculator className="icon" /> Tax & ITR Services
                </div>
                <div className="pill-item-new pill-purple">
                  <FaBriefcase className="icon" /> Employment Law Support
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="col-lg-6 right-col-new">
              <div className="image-wrapper-legal">

                {/* Main Image */}
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&q=80&w=1000"
                  alt="Professional Corporate Consulting"
                  className="legal-hero-img"
                />

                {/* Floating Elements */}
                <div className="floating-badge-legal f-top-right">
                  <div className="f-icon-box green-box"><FaShieldAlt /></div>
                  <div className="f-text">
                    <strong>100%</strong>
                    <span>Compliant</span>
                  </div>
                </div>

                <div className="floating-badge-legal f-bottom-left">
                  <div className="f-icon-box blue-box"><FaFileInvoice /></div>
                  <div className="f-text">
                    <strong>Smart</strong>
                    <span>Documents</span>
                  </div>
                </div>

                {/* Slider Nav Arrows */}
                <div className="slider-controls-right">
                  <button className="btn-nav prev" onClick={() => scroll("left")}>
                    <FaChevronLeft />
                  </button>
                  <button className="btn-nav next" onClick={() => scroll("right")}>
                    <FaChevronRight />
                  </button>
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* BOTTOM SERVICE CARDS (SLIDER) */}
        <div className="slider-track-new mt-5" ref={scrollRef}>
          {services.map((item, index) => (
            <div className="slide-card-new" key={index}>
              <div
                className="card-content-new"
                style={{
                  '--themeColor': item.theme,
                  '--lightColor': item.light
                }}
              >
                <div className="card-top-border"></div>
                <div className="card-icon-box">
                  {item.icon}
                </div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>

                <div className="card-bottom">
                  <Link to="/contact" className="btn-learn-more text-decoration-none">
                    Learn More <FaArrowRight className="ms-1 fs-14" />
                  </Link>
                  <Link to="/contact" className="btn-circle-arrow">
                    <FaArrowRight />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        /* CORE SECTION */
        .legal-section-new {
          padding: 105px 0 120px 0; /* Reduced top padding to close gap with MapBanner */
          background: #f8fafc; /* Very light blue/white base */
          position: relative;
          overflow: hidden;
          font-family: 'Inter', sans-serif;
        }

        /* BACKGROUND DECORATIONS */
        .bg-shape-glow {
          position: absolute;
          top: 50%;
          right: -10%;
          transform: translateY(-50%);
          width: 800px;
          height: 800px;
          background: radial-gradient(circle, rgba(29,78,216,0.04) 0%, rgba(29,78,216,0) 70%);
          z-index: 0;
          border-radius: 50%;
          pointer-events: none;
        }

        .bg-grid-pattern {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-image: radial-gradient(rgba(15, 23, 42, 0.05) 1px, transparent 1px);
          background-size: 30px 30px;
          z-index: 0;
          pointer-events: none;
        }

        /* MAIN CONTAINER */
        .legal-hero-wrapper {
          max-width: 1440px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
        }

        .row {
          margin: 0;
        }

        /* LEFT COLUMN */
        .left-col-new {
          padding-right: 40px;
          padding-bottom: 40px; /* Space before slider */
        }

        /* EYEBROW TAG */
        .sub-tag-new {
          display: inline-flex;
          align-items: center;
          font-size: 12px;
          font-weight: 800;
          color: #165986;
          letter-spacing: 1px;
          margin-bottom: 30px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          padding: 6px 20px 6px 6px;
          border-radius: 40px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.02);
        }

        .icon-circle {
          width: 32px;
          height: 32px;
          background: #e0e7ff;
          color: #4338ca;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 12px;
          font-size: 14px;
        }

        .line-extend {
          display: none; /* Hidden since we are using a pill background now */
        }

        /* HEADING */
        .main-legal-h2 {
          font-size: 64px;
          font-weight: 800;
          color: #0f172a; /* Deep navy */
          line-height: 1.1;
          margin-bottom: 25px;
          letter-spacing: -1.5px;
          max-width: 650px;
        }

        .text-highlight {
          color: #165986; /* Royal blue */
        }

        /* DESCRIPTION */
        .desc-text-new {
          font-size: 19px;
          color: #475569;
          line-height: 1.6;
          margin-bottom: 40px;
          max-width: 600px;
        }

        /* SERVICE BADGES (PILLS) */
        .pills-row-new {
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
        }

        .pill-item-new {
          display: flex;
          align-items: center;
          padding: 12px 20px;
          border-radius: 999px;
          font-size: 15px;
          font-weight: 600;
          color: #0f172a;
          box-shadow: 0 4px 15px rgba(0,0,0,0.02);
          border: 1px solid rgba(255,255,255,0.6);
        }

        .pill-item-new .icon {
          margin-right: 10px;
          font-size: 16px;
        }

        .pill-green {
          background: #dcfce7;
        }
        .pill-green .icon {
          color: #16a34a;
        }

        .pill-cyan {
          background: #e0f2fe;
        }
        .pill-cyan .icon {
          color: #0284c7;
        }

        .pill-purple {
          background: #f3e8ff;
        }
        .pill-purple .icon {
          color: #9333ea;
        }

        /* RIGHT COLUMN */
        .right-col-new {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        /* IMAGE WRAPPER */
        .image-wrapper-legal {
          position: relative;
          width: 100%;
          max-width: 520px;
          height: 480px;
          border-radius: 32px;
          z-index: 2;
        }

        .legal-hero-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 32px;
          border: 6px solid #ffffff;
          box-shadow: 0 25px 50px rgba(15, 23, 42, 0.08);
          position: relative;
          z-index: 2;
        }

        /* FLOATING DECORATIVE BADGES */
        .floating-badge-legal {
          position: absolute;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(8px);
          border-radius: 16px;
          padding: 12px 20px;
          display: flex;
          align-items: center;
          box-shadow: 0 15px 35px rgba(15, 23, 42, 0.08);
          border: 1px solid rgba(255,255,255,0.8);
          z-index: 3;
          animation: floatLegal 6s ease-in-out infinite;
        }

        .f-top-right {
          top: 30px;
          right: -30px;
          animation-delay: 0s;
        }

        .f-bottom-left {
          bottom: 40px;
          left: -40px;
          animation-delay: 3s;
        }

        @keyframes floatLegal {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-12px); }
        }

        .f-icon-box {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          margin-right: 12px;
        }
        
        .green-box { background: #dcfce7; color: #16a34a; }
        .blue-box { background: #dbeafe; color: #165986; }

        .f-text {
          display: flex;
          flex-direction: column;
        }

        .f-text strong {
          color: #0f172a;
          font-size: 14px;
          font-weight: 800;
          line-height: 1.2;
        }

        .f-text span {
          color: #64748b;
          font-size: 12px;
          font-weight: 500;
        }

        /* SLIDER CONTROLS NEAR IMAGE */
        .slider-controls-right {
          position: absolute;
          bottom: -25px;
          right: 20px;
          display: flex;
          gap: 15px;
          z-index: 4;
        }

        .btn-nav {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 10px 25px rgba(15, 23, 42, 0.1);
        }

        .btn-nav.prev {
          background: #ffffff;
          color: #165986;
          border: 1px solid #f1f5f9;
        }

        .btn-nav.prev:hover {
          background: #f8fafc;
        }

        .btn-nav.next {
          background: #165986;
          color: #ffffff;
          border: none;
        }

        .btn-nav.next:hover {
          background: #1e3a8a;
          box-shadow: 0 10px 25px rgba(29, 78, 216, 0.3);
        }

        /* BOTTOM SLIDER CARDS (Existing aesthetics updated) */
        .slider-track-new {
          display: flex;
          gap: 30px;
          overflow-x: auto;
          padding: 20px 0 50px 0;
          scroll-snap-type: x mandatory;
          scroll-behavior: smooth;
          scrollbar-width: none;
          max-width: 1440px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
        }

        .slider-track-new::-webkit-scrollbar {
          display: none;
        }

        .slide-card-new {
          min-width: 360px;
          max-width: 360px;
          scroll-snap-align: start;
        }

        .card-content-new {
          background: #ffffff;
          border-radius: 24px;
          padding: 40px 30px;
          height: 100%;
          position: relative;
          box-shadow: 0 10px 35px rgba(15, 23, 42, 0.04);
          display: flex;
          flex-direction: column;
          border: 1px solid #f1f5f9;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .card-content-new:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 45px rgba(15, 23, 42, 0.08);
        }

        .card-top-border {
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 80%;
          height: 5px;
          background: var(--themeColor);
          border-radius: 0 0 10px 10px;
        }

        .card-icon-box {
          width: 65px;
          height: 65px;
          background: var(--lightColor);
          color: var(--themeColor);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 30px;
          margin-bottom: 25px;
        }

        .card-content-new h3 {
          font-size: 22px;
          font-weight: 800;
          color: #165986;
          margin-bottom: 15px;
        }

        .card-content-new p {
          color: #64748b;
          font-size: 15.5px;
          line-height: 1.6;
          margin-bottom: 35px;
          flex-grow: 1;
        }

        .card-bottom {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .btn-learn-more {
          background: var(--themeColor);
          color: #ffffff;
          border: none;
          padding: 12px 24px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 700;
          display: flex;
          align-items: center;
          transition: opacity 0.3s ease;
        }

        .btn-learn-more:hover {
          opacity: 0.9;
          color: #ffffff;
        }

        .btn-circle-arrow {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: var(--lightColor);
          color: var(--themeColor);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          transition: transform 0.3s ease;
        }

        .btn-circle-arrow:hover {
          transform: scale(1.1);
        }

        .fs-14 {
          font-size: 14px;
        }

        /* RESPONSIVE DESIGN */
        @media (max-width: 1440px) {
          .main-legal-h2 { font-size: 58px; }
          .image-wrapper-legal { max-width: 480px; height: 440px; }
        }

        @media (max-width: 1200px) {
          .main-legal-h2 { font-size: 48px; }
          .image-wrapper-legal { max-width: 420px; height: 380px; }
          .left-col-new { padding-right: 20px; }
          .slider-controls-right { bottom: -20px; right: 0; }
        }

        @media (max-width: 992px) {
          .left-col-new { padding-right: 15px; margin-bottom: 60px; text-align: center; }
          .sub-tag-new { justify-content: center; }
          .main-legal-h2 { font-size: 44px; margin: 0 auto 25px auto; }
          .desc-text-new { margin: 0 auto 30px auto; }
          .pills-row-new { justify-content: center; }
          
          .image-wrapper-legal { margin: 0 auto; }
          .f-top-right { right: -10px; top: 10px; }
          .f-bottom-left { left: -10px; bottom: 20px; }
        }

        @media (max-width: 768px) {
          .legal-section-new { padding: 60px 0 80px 0; }
          .main-legal-h2 { font-size: 36px; }
          .desc-text-new { font-size: 16px; }
          .pill-item-new { font-size: 14px; padding: 10px 16px; }
          
          .image-wrapper-legal { max-width: 100%; height: 320px; border-radius: 24px; }
          .legal-hero-img { border-radius: 24px; border-width: 4px; }
          
          .floating-badge-legal { padding: 8px 14px; }
          .f-text strong { font-size: 12px; }
          .f-text span { font-size: 10px; }
          .f-icon-box { width: 28px; height: 28px; font-size: 14px; }
          
          .slider-controls-right { display: none; /* Hide absolute arrows on mobile, or move to top */ }
          .slide-card-new { min-width: 280px; max-width: 280px; }
        }
        `
      }} />
    </section>
  );
}

export default PopularGuide;